import React, { ReactElement, useState } from 'react'
import styled from 'styled-components'
import CountrySelector from './CountrySelector'
import { noOp, simpleEmailValidation } from '../util/Util'
import { RADOM_COLORS } from '../util/Constants'
import { TRAVEL_RULES_COMPLIANCE_BANNER } from '../util/BoilerPlate'
import { PrimaryButton } from './Button'
import { emptyTravelRuleFields, TravelRuleFields, TravelRuleFieldsCollected, CheckoutType } from './Checkout'
import { InputLabel, TextInput } from './Input'
import Switch from './Switch'

// ---------------------------------------------------------------------------------------------------------------------
const AddressRow = styled.div`
  display: flex;
  gap: 10px;
`

const TravelRuleFieldsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 5px;
  margin: auto;
  max-width: 520px;
  font-size: 14px;
`

const TravelRuleFieldsRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: 5px;
`

// ---------------------------------------------------------------------------------------------------------------------
interface IProps {
  travelRuleFieldsCollected: TravelRuleFieldsCollected
  accountType?: string
  onTravelRuleFieldsComplete: (trFields: TravelRuleFields) => void
}

// Alternate label text for individuals and businesses
const labelText: Record<string, [string, string]> = {
  name: ['Name', 'Representative Name'],
  email: ['Email Address', 'Email Address'],
  billingAddress: ['Billing Address', 'Company Billing Address'],
  country: ['Country', 'Company Registration Country'],
  company: ['', 'Company Name']
}
const fetchLabelText = (labelName: string, isBusiness: boolean): string => labelText[labelName][+isBusiness]

function generateInputLabel(labelName: string, isBusiness: boolean): ReactElement {
  return <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: '15px' }}>
    <InputLabel>{fetchLabelText(labelName, isBusiness)}</InputLabel>
  </div>
}

const isAccountTypeSwitchDisabled = (trfsCollected: TravelRuleFieldsCollected, accountType?: string): boolean =>
  (trfsCollected.accountType ?? accountType) === 'Business' && trfsCollected.companyNameCollected

// ---------------------------------------------------------------------------------------------------------------------
const TravelRuleFieldsWidget = (props: IProps): React.ReactElement => {
  const initialAccountType = props.travelRuleFieldsCollected.accountType ?? props.accountType
  const [isBusiness, setIsBusiness] = useState(initialAccountType === 'Business')
  const toggleIsBusiness = (): void => {
    const state = isBusiness
    setIsBusiness(!state)
  }

  const [collectedTravelRuleFields, setCollectedTravelRuleFields] = useState(emptyTravelRuleFields())
  const [errorMessage, setErrorMessage] = useState<string>()
  const accountTypeSwitchDisabled = isAccountTypeSwitchDisabled(props.travelRuleFieldsCollected, props.accountType)

  const addressBlockRequired = !props.travelRuleFieldsCollected.dateOfBirthCollected && (
    !props.travelRuleFieldsCollected.addressLine1Collected ||
    !props.travelRuleFieldsCollected.townOrCityCollected ||
    !props.travelRuleFieldsCollected.postalCodeCollected ||
    !props.travelRuleFieldsCollected.stateOrProvinceCollected
  )

  const setTRFsFromHtmlElement = (
    // The HTML input element from which the value is being collected
    inputEl: any,
    // The property to be updated in TravelRuleFields
    trfName: string,
    // If present, the input value will be modified by passing it to this closure
    modifierFn?: (val: string) => string
  ): void => {
    const newTrFields = { ...collectedTravelRuleFields }
    const inputValue = (inputEl.target as HTMLInputElement).value
    newTrFields[trfName] = modifierFn ? modifierFn(inputValue) : inputValue
    setCollectedTravelRuleFields(newTrFields)
  }

  return <TravelRuleFieldsContainer>
    {
      // ---------------------------------------------------------------------------------------------------------------
      // Error toast
      errorMessage && errorMessage.length > 0 &&
      <div style={{
        backgroundColor: RADOM_COLORS.ERROR,
        padding: 15,
        marginBottom: 10,
        borderRadius: 5,
        fontSize: 14,
        color: 'white',
        overflowWrap: 'break-word'
      }}>
        {errorMessage}
      </div>
    }

    {
      // ---------------------------------------------------------------------------------------------------------------
      // Individual/Business switch
      <div
        style={{ cursor: 'pointer', userSelect: 'none', display: 'flex', alignItems: 'center', marginTop: 20 }}
        onClick={accountTypeSwitchDisabled ? noOp : toggleIsBusiness}>
        <Switch
          checked={isBusiness}
          onClick={accountTypeSwitchDisabled ? noOp : toggleIsBusiness}
          disabled={accountTypeSwitchDisabled}
        />
        <div style={{ flex: 1, color: RADOM_COLORS.BLACK, fontSize: 14, overflowWrap: 'break-word', marginLeft: 10 }}>
          Paying as a business?
        </div>
      </div>
    }

    {
      // ---------------------------------------------------------------------------------------------------------------
      // Company Name - only collected if paying as a business
      isBusiness && !props.travelRuleFieldsCollected.companyNameCollected &&
      <TravelRuleFieldsRow key="missingCompanyName">
        {generateInputLabel('company', true)}
        {
          <TextInput
            key='trCompanyName'
            name='organization'
            autoComplete='organization'
            value={collectedTravelRuleFields.companyName}
            placeholder={'Enter your company name'}
            required={true}
            onInput={el => setTRFsFromHtmlElement(el, 'companyName')}
          />
        }
      </TravelRuleFieldsRow>
    }

    {
      // ---------------------------------------------------------------------------------------------------------------
      // Name
      !props.travelRuleFieldsCollected.nameCollected &&
      <TravelRuleFieldsRow key="missingName">
        {generateInputLabel('name', isBusiness)}
        {
          <TextInput
            key='trName'
            name='name'
            autoComplete='name'
            value={collectedTravelRuleFields.name}
            placeholder={'Enter name'}
            required={true}
            onInput={el => setTRFsFromHtmlElement(el, 'name')}
          />
        }
      </TravelRuleFieldsRow>
    }

    {
      // ---------------------------------------------------------------------------------------------------------------
      // Email address
      !props.travelRuleFieldsCollected.emailAddressCollected &&
      <TravelRuleFieldsRow key="missingEmailAddress">
        {generateInputLabel('email', isBusiness)}
        {
          <TextInput
            key='trEmailAddress'
            name='email'
            autoComplete='email'
            value={collectedTravelRuleFields.emailAddress}
            placeholder={'Enter email address'}
            required={true}
            onInput={el => setTRFsFromHtmlElement(el, 'emailAddress', s => s.toLowerCase())}
          />
        }
      </TravelRuleFieldsRow>
    }

    {
      // ---------------------------------------------------------------------------------------------------------------
      // Billing Address
      // addressLine2 is optional
      addressBlockRequired &&
       generateInputLabel('billingAddress', isBusiness)
    }

    {
      // Address line 1
      addressBlockRequired && !props.travelRuleFieldsCollected.addressLine1Collected &&
      <TextInput
        key='trAddressLine1'
        name='address-line1'
        autoComplete='address-line1'
        placeholder='Address line 1'
        onInput={el => setTRFsFromHtmlElement(el, 'addressLine1')}
        style={{ margin: '3px 0px' }}
      />
    }

    {
      // Address line 2
      addressBlockRequired && !props.travelRuleFieldsCollected.addressLine2Collected &&
      <TextInput
        key='trAddressLine2'
        name='address-line2'
        autoComplete='address-line2'
        placeholder='Address line 2'
        onInput={el => setTRFsFromHtmlElement(el, 'addressLine2')}
        style={{ margin: '3px 0px' }}
      />
    }

    {
      // Town/City or Postal Code
      (addressBlockRequired && (!props.travelRuleFieldsCollected.townOrCityCollected ||
        !props.travelRuleFieldsCollected.postalCodeCollected)) &&
      <AddressRow>
        <>
          {addressBlockRequired && !props.travelRuleFieldsCollected.townOrCityCollected &&
            <TextInput
              key='trTownOrCity'
              name='address-level2'
              autoComplete='address-level2'
              placeholder='Town/City'
              onInput={el => setTRFsFromHtmlElement(el, 'townOrCity')}
              style={{ margin: '3px 0px', width: '50%' }}
            />}

          {addressBlockRequired && !props.travelRuleFieldsCollected.postalCodeCollected &&
            <TextInput
              key='trPostalCode'
              name='postal-code'
              autoComplete='postal-code'
              placeholder='Postal Code/Zip'
              onInput={el => setTRFsFromHtmlElement(el, 'postalCode')}
              style={{ margin: '3px 0px', width: '50%' }}
            />}
        </>
      </AddressRow>
    }

    {
      // State/Province
      addressBlockRequired && !props.travelRuleFieldsCollected.stateOrProvinceCollected &&
      <TextInput
        key='trStateOrProvince'
        name='address-level1'
        autoComplete='address-level1'
        placeholder='State/Province'
        onInput={el => setTRFsFromHtmlElement(el, 'stateOrProvince')}
        style={{ margin: '3px 0px' }}
      />
    }

    {
      // ---------------------------------------------------------------------------------------------------------------
      // Country of residence
      !props.travelRuleFieldsCollected.countryOfResidenceCollected &&
      <TravelRuleFieldsRow key="missingCountryOfResidence" style={{ margin: '3px 0px' }}>
        {
          <CountrySelector
            selectedCountry={collectedTravelRuleFields.countryOfResidence}
            onCountrySelected={country => {
              if (country) {
                const newTrFields = { ...collectedTravelRuleFields }
                newTrFields.countryOfResidence = country.name
                setCollectedTravelRuleFields(newTrFields)
              }
            }}
          />
        }
      </TravelRuleFieldsRow>
    }

    {
      // ---------------------------------------------------------------------------------------------------------------
      // Compliance banner
      TRAVEL_RULES_COMPLIANCE_BANNER
    }

    {
      // ---------------------------------------------------------------------------------------------------------------
      // Blue "Continue" button
      <PrimaryButton

        style={{ background: RADOM_COLORS.BLUE4, width: '100%', marginTop: '25px' }}
        onClick={() => {
          collectedTravelRuleFields.accountType = isBusiness ? CheckoutType.Business : CheckoutType.Individual

          let missingField: string | undefined

          if (isBusiness && !props.travelRuleFieldsCollected.companyNameCollected &&
            !collectedTravelRuleFields.companyName) {
            missingField = 'company name'
          } else if (!props.travelRuleFieldsCollected.nameCollected && !collectedTravelRuleFields.name) {
            missingField = 'name'
          } else if (!props.travelRuleFieldsCollected.emailAddressCollected &&
            (!collectedTravelRuleFields.emailAddress ||
              !simpleEmailValidation(collectedTravelRuleFields.emailAddress))) {
            missingField = 'email address'
          } else if (addressBlockRequired && !props.travelRuleFieldsCollected.addressLine1Collected &&
            !collectedTravelRuleFields.addressLine1) {
            missingField = 'address line 1'
          } else if (addressBlockRequired && !props.travelRuleFieldsCollected.townOrCityCollected &&
            !collectedTravelRuleFields.townOrCity) {
            missingField = 'town or city'
          } else if (addressBlockRequired && !props.travelRuleFieldsCollected.postalCodeCollected &&
            !collectedTravelRuleFields.postalCode) {
            missingField = 'postal code'
          } else if (addressBlockRequired && !props.travelRuleFieldsCollected.stateOrProvinceCollected &&
            !collectedTravelRuleFields.stateOrProvince) {
            missingField = 'state or province'
          } else if (!props.travelRuleFieldsCollected.countryOfResidenceCollected &&
            !collectedTravelRuleFields.countryOfResidence) {
            missingField = 'country of residence'
          }

          if (missingField) {
            setErrorMessage(`Please enter ${missingField}.`)
          } else {
            props.onTravelRuleFieldsComplete(collectedTravelRuleFields)
            setErrorMessage('')
          }
        }}
      >
        Continue
      </PrimaryButton>
    }
  </TravelRuleFieldsContainer >
}

export default TravelRuleFieldsWidget
